import { useLazyQuery } from '@apollo/client';
import { SEARCH_PARK_ZONES_BY_TEXT } from '@queries/searchParkZonesByText';
import { SEARCH_PARK_ZONES_LTP_BY_TEXT } from '@queries/searchParkZonesLTPByText';
import { SEARCH_EV_CONNECTORS } from 'graphql/queries/searchEvConnectors';
import { useCallback, useLayoutEffect, useMemo } from 'react';
import { useMapStore } from 'stores/mapStore';
import {
  Query,
  QuerySearchEvConnectorsArgs,
  QuerySearchParkZonesByTextArgs,
  QuerySearchParkZonesLtpByTextArgs,
} from 'types/generatedSchemaTypes';

type UseParkingZoneSearchProps = {
  searchTerm: string;
  withLTP?: boolean;
};

type UseParkingZoneSearchReturn = {
  loading: boolean;
  error: any;
  parkZones: Query | undefined;
  evConnectors: Query | undefined;
  hasZoneResults: boolean;
  hasConnectorResults: boolean;
  noResults: boolean;
};

export const useParkingZoneSearch = ({
  searchTerm,
  withLTP = false,
}: UseParkingZoneSearchProps): UseParkingZoneSearchReturn => {
  // Memoize the query selection to prevent unnecessary re-renders
  const parkZoneQuery = useMemo(
    () => (withLTP ? SEARCH_PARK_ZONES_LTP_BY_TEXT : SEARCH_PARK_ZONES_BY_TEXT),
    [withLTP]
  );

  const [getZonesByText, { error, loading, data: parkZones }] = useLazyQuery<
    Query,
    QuerySearchParkZonesByTextArgs | QuerySearchParkZonesLtpByTextArgs
  >(parkZoneQuery, {
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });

  const [
    getEvConnectorsByText,
    { error: chargerError, loading: chargerLoading, data: evConnectors },
  ] = useLazyQuery<Query, QuerySearchEvConnectorsArgs>(SEARCH_EV_CONNECTORS, {
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });

  const executeSearch = useCallback(() => {
    if (searchTerm.length >= 2) {
      const currentLocation = useMapStore.getState().userLocation;

      getZonesByText({
        variables: {
          input: {
            searchWord: searchTerm,
            coordinates: currentLocation,
            size: 20,
          },
        },
      });

      if (!withLTP) {
        getEvConnectorsByText({
          variables: {
            publicDisplayId: searchTerm,
          },
        });
      }
    }
  }, [searchTerm, withLTP, getZonesByText, getEvConnectorsByText]);

  useLayoutEffect(() => {
    executeSearch();
  }, [executeSearch]);

  const hasErrors = error || chargerError;
  // Calculate if there are results for zones and connectors
  const hasZoneResults = useMemo(() => {
    if (!parkZones) {
      return false;
    }
    return withLTP
      ? (parkZones.searchParkZonesLTPByText?.content?.length ?? 0) > 0
      : (parkZones.searchParkZonesByText?.content?.length ?? 0) > 0;
  }, [parkZones, withLTP]);

  const hasConnectorResults = useMemo(
    () => (evConnectors?.searchEvConnectors?.length ?? 0) > 0,
    [evConnectors]
  );

  const noResults = useMemo(
    () => !hasZoneResults && !hasConnectorResults,
    [hasZoneResults, hasConnectorResults]
  );

  // Memoize the return object to prevent unnecessary re-renders
  return useMemo(
    () => ({
      loading: loading || chargerLoading,
      error: hasErrors,
      parkZones,
      evConnectors,
      hasZoneResults,
      hasConnectorResults,
      noResults,
    }),
    [
      loading,
      chargerLoading,
      hasErrors,
      parkZones,
      evConnectors,
      hasZoneResults,
      hasConnectorResults,
      noResults,
    ]
  );
};
